// Quick-filter pill row used at the top of admin tables (Locations,
// Inventory, Totes, Products) to flip the view between an "All"
// scope and a single discrete value (warehouse, status, type, etc.)
// without diving into a select.
//
// A caller that passes `selected` instead gets a multi-select row: each
// pill toggles its value in or out of the set, "All" clears it, and the
// row reads as All while the set is empty (Pallets Created › Colour,
// Alex, 2026-10-21).
//
// Generic on the option type so callers keep their own narrow string
// unions; the component only needs label + value pairs.
import type { ReactNode } from "react";
import { useTranslation } from "../../i18n";

export interface FilterPillOption {
    value: string;
    label: string;
    /** Optional small count rendered after the label. */
    count?: number;
}

interface FilterPillsProps {
    options: FilterPillOption[];
    /** Single-select: the active value ("" = All). Ignored when `selected` is given. */
    value?: string;
    onChange?: (next: string) => void;
    /** Multi-select: the active set. A pill toggles its value; All empties the set. */
    selected?: string[];
    onSelectedChange?: (next: string[]) => void;
    /** Label rendered before the pills (e.g. "Warehouse"). Optional. */
    labelKey?: string;
    /** Label for the "All" pill that resets the filter. Defaults to t("status_all"). */
    allLabelKey?: string;
    /** Total count to render alongside the All pill. */
    allCount?: number;
    /** Drop the "All" pill (e.g. a warehouse row for someone who may not cross warehouses). */
    hideAll?: boolean;
    /** Optional trailing content rendered on the far right of the row
     *  (e.g. an Export button on the Logs page). Pushed to the end via
     *  margin-left:auto so it stays right-aligned regardless of pill count. */
    trailing?: ReactNode;
    /** Optional extra class on the wrapper for page-specific spacing /
     *  pill density (e.g. .warehouse-admin-pills on the warehouse admin
     *  pages so they feel unified with the chip-styled filter row). */
    className?: string;
}

const FilterPills = ({ options, value = "", onChange, selected, onSelectedChange, labelKey, allLabelKey, allCount, hideAll, trailing, className }: FilterPillsProps) => {
    const { t } = useTranslation();
    const wrapClass = `filter-pills${className ? ` ${className}` : ""}`;
    const multi = selected !== undefined;
    const isActive = (v: string) => (multi ? selected.includes(v) : value === v);
    const allActive = multi ? selected.length === 0 : value === "";
    const pick = (v: string) => {
        if (!multi) { onChange?.(v); return; }
        if (v === "") { onSelectedChange?.([]); return; }
        onSelectedChange?.(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
    };
    return (
        <div className={wrapClass} role="tablist" aria-multiselectable={multi || undefined}>
            {labelKey && <span className="filter-pills-label">{t(labelKey)}</span>}
            {!hideAll && <button
                type="button"
                role="tab"
                aria-selected={allActive}
                className={`filter-pill${allActive ? " filter-pill--active" : ""}`}
                onClick={() => pick("")}
            >
                {t(allLabelKey ?? "status_all")}
                {typeof allCount === "number" && (
                    <span className="filter-pill-count">{allCount}</span>
                )}
            </button>}
            {options.map(opt => (
                <button
                    key={opt.value}
                    type="button"
                    role="tab"
                    aria-selected={isActive(opt.value)}
                    className={`filter-pill${isActive(opt.value) ? " filter-pill--active" : ""}`}
                    onClick={() => pick(opt.value)}
                >
                    {opt.label}
                    {typeof opt.count === "number" && (
                        <span className="filter-pill-count">{opt.count}</span>
                    )}
                </button>
            ))}
            {trailing && <div className="filter-pills-trailing">{trailing}</div>}
        </div>
    );
};

export default FilterPills;
